<template>
  <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
    <h3 class="text-sm font-semibold text-gray-900">
      Block Editor
    </h3>
    <p
      v-if="!blockValue"
      class="mt-3 text-xs text-gray-500"
    >
      Select a block on the canvas to edit its content.
    </p>
    <div
      v-else
      class="mt-3 space-y-4"
    >
      <div class="text-xs font-medium text-gray-500">
        {{ label }}
      </div>

      <div v-if="blockValue.type === 'heading'">
        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Level</label>
            <select
              v-model="blockValue.data.level"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            >
              <option :value="1">
                H1
              </option>
              <option :value="2">
                H2
              </option>
              <option :value="3">
                H3
              </option>
              <option :value="4">
                H4
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Align</label>
            <select
              v-model="blockValue.data.align"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            >
              <option value="left">
                Left
              </option>
              <option value="center">
                Center
              </option>
              <option value="right">
                Right
              </option>
            </select>
          </div>
        </div>
      </div>

      <div v-else-if="blockValue.type === 'text'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Align</label>
        <select
          v-model="blockValue.data.align"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
        >
          <option value="left">
            Left
          </option>
          <option value="center">
            Center
          </option>
          <option value="right">
            Right
          </option>
        </select>
      </div>

      <div v-else-if="blockValue.type === 'image'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Image URL</label>
        <input
          v-model="blockValue.data.src"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Alt text</label>
            <input
              v-model="blockValue.data.alt"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Caption</label>
            <input
              v-model="blockValue.data.caption"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
        </div>
      </div>

      <div v-else-if="blockValue.type === 'button'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Button text</label>
        <input
          v-model="blockValue.data.text"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">URL</label>
            <input
              v-model="blockValue.data.href"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Variant</label>
            <select
              v-model="blockValue.data.variant"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            >
              <option value="primary">
                Primary
              </option>
              <option value="secondary">
                Secondary
              </option>
              <option value="outline">
                Outline
              </option>
            </select>
          </div>
        </div>
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Align</label>
          <select
            v-model="blockValue.data.align"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="left">
              Left
            </option>
            <option value="center">
              Center
            </option>
            <option value="right">
              Right
            </option>
          </select>
        </div>
      </div>

      <div v-else-if="blockValue.type === 'quote'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Quote</label>
        <textarea
          v-model="blockValue.data.text"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="3"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Author</label>
          <input
            v-model="blockValue.data.author"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            type="text"
          >
        </div>
      </div>

      <div v-else-if="blockValue.type === 'embed'">
        <label class="block mb-2 text-xs font-medium text-gray-600">YouTube URL</label>
        <input
          v-model="blockValue.data.url"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
      </div>

      <div v-else-if="blockValue.type === 'divider'">
        <div class="grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Style</label>
            <select
              v-model="blockValue.data.style"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            >
              <option value="solid">
                Solid
              </option>
              <option value="dashed">
                Dashed
              </option>
              <option value="dotted">
                Dotted
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Spacing</label>
            <input
              v-model.number="blockValue.data.spacing"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="number"
              min="0"
            >
          </div>
        </div>
      </div>

      <div v-else-if="blockValue.type === 'html'">
        <label class="block mb-2 text-xs font-medium text-gray-600">HTML</label>
        <textarea
          v-model="blockValue.data.html"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="5"
        />
      </div>
      <div v-else-if="blockValue.type === 'hero'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Headline</label>
        <input
          v-model="blockValue.data.headline"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Subheadline</label>
        <textarea
          v-model="blockValue.data.subheadline"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="3"
        />
        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">CTA Text</label>
            <input
              v-model="blockValue.data.ctaText"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">CTA URL</label>
            <input
              v-model="blockValue.data.ctaUrl"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
        </div>
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Align</label>
          <select
            v-model="blockValue.data.align"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="left">
              Left
            </option>
            <option value="center">
              Center
            </option>
            <option value="right">
              Right
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'columns'">
        <div class="grid gap-3 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Columns</label>
            <select
              v-model="blockValue.data.columns"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            >
              <option :value="2">
                2 Columns
              </option>
              <option :value="3">
                3 Columns
              </option>
              <option :value="4">
                4 Columns
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Gap (px)</label>
            <input
              v-model="blockValue.data.gap"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="number"
              min="8"
              max="48"
            >
          </div>
        </div>
        <p class="mt-3 text-xs text-gray-500">
          Drop blocks into each column from the left panel.
        </p>
      </div>
      <div v-else-if="blockValue.type === 'gallery'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Upload images</label>
        <input
          class="block w-full rounded-lg border border-dashed border-gray-300 bg-white p-2 text-xs text-gray-700"
          type="file"
          multiple
          accept="image/*"
          @change="onGalleryUpload"
        >
        <div
          v-if="galleryImages.length"
          class="mt-3 grid grid-cols-2 gap-2"
        >
          <div
            v-for="(img, idx) in galleryImages"
            :key="`gal-edit-${idx}`"
            class="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
          >
            <img
              :src="img"
              alt="Gallery preview"
              class="h-20 w-full object-cover"
            >
            <button
              class="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm opacity-0 transition group-hover:opacity-100"
              type="button"
              title="Remove"
              @click="removeGalleryImage(idx)"
            >
              <svg
                class="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M5 6l1 14h12l1-14" />
              </svg>
            </button>
          </div>
        </div>
        <p
          v-else
          class="mt-3 text-xs text-gray-500"
        >
          No images yet. Upload files to populate the gallery.
        </p>
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
            <option value="stack">
              Stack
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'video'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Video URL</label>
        <input
          v-model="blockValue.data.url"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Caption</label>
        <input
          v-model="blockValue.data.caption"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
      </div>
      <div v-else-if="blockValue.type === 'logo_cloud'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Logos (one per line)</label>
        <textarea
          v-model="blockValue.data.logos"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
          placeholder="Logo name|https://logo.url"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
            <option value="stack">
              Stack
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'steps'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Title</label>
        <input
          v-model="blockValue.data.title"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Steps (one per line)</label>
        <textarea
          v-model="blockValue.data.steps"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
          placeholder="1|First step"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="stack">
              Stack
            </option>
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'form'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Title</label>
        <input
          v-model="blockValue.data.title"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Description</label>
        <textarea
          v-model="blockValue.data.description"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="3"
        />
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Fields</label>
        <div class="space-y-3">
          <div
            v-for="(field, idx) in formFields"
            :key="field.id"
            class="rounded-lg border border-gray-200 bg-white p-3"
          >
            <div class="grid gap-2 sm:grid-cols-2">
              <div>
                <label class="block mb-1 text-[11px] font-medium text-gray-600">Label</label>
                <input
                  v-model="field.label"
                  class="block w-full rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
                  type="text"
                >
              </div>
              <div>
                <label class="block mb-1 text-[11px] font-medium text-gray-600">Type</label>
                <select
                  v-model="field.type"
                  class="block w-full rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
                >
                  <option value="text">
                    Text
                  </option>
                  <option value="email">
                    Email
                  </option>
                  <option value="tel">
                    Phone
                  </option>
                  <option value="number">
                    Number
                  </option>
                  <option value="textarea">
                    Textarea
                  </option>
                  <option value="select">
                    Select
                  </option>
                </select>
              </div>
            </div>
            <div class="mt-2 grid gap-2 sm:grid-cols-2">
              <div class="flex items-center gap-2">
                <input
                  v-model="field.required"
                  type="checkbox"
                  class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                >
                <span class="text-xs text-gray-600">Required</span>
              </div>
              <button
                class="text-xs text-red-600 hover:text-red-700"
                type="button"
                @click="removeFormField(idx)"
              >
                Remove
              </button>
            </div>
            <div
              v-if="field.type === 'select'"
              class="mt-2"
            >
              <label class="block mb-1 text-[11px] font-medium text-gray-600">Options (one per line)</label>
              <textarea
                v-model="field.options"
                class="block w-full rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
                rows="2"
              />
            </div>
          </div>
          <button
            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            type="button"
            @click="addFormField"
          >
            Add field
          </button>
        </div>
        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
            <select
              v-model="blockValue.data.layout"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            >
              <option value="stack">
                Stack
              </option>
              <option value="grid2">
                2 Columns
              </option>
              <option value="grid3">
                3 Columns
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Button Text</label>
            <input
              v-model="blockValue.data.buttonText"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'contact'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Title</label>
        <input
          v-model="blockValue.data.title"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Description</label>
        <textarea
          v-model="blockValue.data.description"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="3"
        />
        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Email Label</label>
            <input
              v-model="blockValue.data.emailLabel"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
          <div>
            <label class="block mb-2 text-xs font-medium text-gray-600">Message Label</label>
            <input
              v-model="blockValue.data.messageLabel"
              class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
              type="text"
            >
          </div>
        </div>
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Button Text</label>
          <input
            v-model="blockValue.data.buttonText"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
            type="text"
          >
        </div>
      </div>
      <div v-else-if="blockValue.type === 'feature_list'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Title</label>
        <input
          v-model="blockValue.data.title"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Items (one per line)</label>
        <textarea
          v-model="blockValue.data.items"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="stack">
              Stack
            </option>
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'stats'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Stats (value|label per line)</label>
        <textarea
          v-model="blockValue.data.items"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="grid3">
              3 Columns
            </option>
            <option value="grid2">
              2 Columns
            </option>
            <option value="stack">
              Stack
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'timeline'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Items (title|detail per line)</label>
        <textarea
          v-model="blockValue.data.items"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
      </div>
      <div v-else-if="blockValue.type === 'pricing'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Plan Title</label>
        <input
          v-model="blockValue.data.title"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Price</label>
        <input
          v-model="blockValue.data.price"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Features (one per line)</label>
        <textarea
          v-model="blockValue.data.features"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="stack">
              Stack
            </option>
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'cta'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Text</label>
        <input
          v-model="blockValue.data.text"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Button Text</label>
        <input
          v-model="blockValue.data.buttonText"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
        <label class="block mb-2 text-xs font-medium text-gray-600 mt-3">Button URL</label>
        <input
          v-model="blockValue.data.buttonUrl"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          type="text"
        >
      </div>
      <div v-else-if="blockValue.type === 'testimonials'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Testimonials (quote|name per line)</label>
        <textarea
          v-model="blockValue.data.items"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="stack">
              Stack
            </option>
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'team'">
        <label class="block mb-2 text-xs font-medium text-gray-600">Team (name|role per line)</label>
        <textarea
          v-model="blockValue.data.members"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
        <div class="mt-3">
          <label class="block mb-2 text-xs font-medium text-gray-600">Layout</label>
          <select
            v-model="blockValue.data.layout"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="grid2">
              2 Columns
            </option>
            <option value="grid3">
              3 Columns
            </option>
            <option value="stack">
              Stack
            </option>
          </select>
        </div>
      </div>
      <div v-else-if="blockValue.type === 'faq'">
        <label class="block mb-2 text-xs font-medium text-gray-600">FAQ (question|answer per line)</label>
        <textarea
          v-model="blockValue.data.items"
          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          rows="4"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent} from 'vue';

export default defineComponent({
  name: 'BlockEditor',
  props: {
    block: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:block'],
  setup(props) {
    const blockValue = computed({
      get: () => props.block,
      set: (value) => {
        if (!value) return;
        // keep same reference but emit so parent can react
      },
    });

    const label = computed(() => {
      if (!blockValue.value) return '';
      return String(blockValue.value.type || 'block').toUpperCase();
    });

    const galleryImages = computed(() => {
      if (!blockValue.value || blockValue.value.type !== 'gallery') return [];
      const images = (blockValue.value.data as any).images;
      if (Array.isArray(images)) return images;
      if (typeof images === 'string') {
        return images
          .split('\n')
          .map((line: string) => line.trim())
          .filter(Boolean);
      }
      return [];
    });

    const syncGalleryImages = (images: string[]) => {
      if (!blockValue.value || blockValue.value.type !== 'gallery') return;
      (blockValue.value.data as any).images = images;
    };

    const onGalleryUpload = async (event: Event) => {
      const input = event.target as HTMLInputElement | null;
      if (!input?.files || !input.files.length) return;
      const files = Array.from(input.files);
      const dataUrls = await Promise.all(
        files.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(String(reader.result || ''));
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(file);
            })
        )
      );
      const merged = [...galleryImages.value, ...dataUrls].filter(Boolean);
      syncGalleryImages(merged);
      input.value = '';
    };

    const removeGalleryImage = (index: number) => {
      const next = [...galleryImages.value];
      next.splice(index, 1);
      syncGalleryImages(next);
    };

    const formFields = computed(() => {
      if (!blockValue.value || blockValue.value.type !== 'form') return [];
      const fields = (blockValue.value.data as any).fields;
      return Array.isArray(fields) ? fields : [];
    });

    const addFormField = () => {
      if (!blockValue.value || blockValue.value.type !== 'form') return;
      const fields = (blockValue.value.data as any).fields;
      if (!Array.isArray(fields)) {
        (blockValue.value.data as any).fields = [];
      }
      (blockValue.value.data as any).fields.push({
        id: `fld_${Math.random().toString(36).slice(2, 10)}`,
        label: 'New field',
        type: 'text',
        required: false,
        options: '',
      });
    };

    const removeFormField = (index: number) => {
      if (!blockValue.value || blockValue.value.type !== 'form') return;
      const fields = (blockValue.value.data as any).fields;
      if (!Array.isArray(fields)) return;
      fields.splice(index, 1);
    };

    return {
      label,
      blockValue,
      galleryImages,
      onGalleryUpload,
      removeGalleryImage,
      formFields,
      addFormField,
      removeFormField,
    };
  },
});
</script>
